import React from 'react';
import { clsx } from 'clsx';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { SearchBar } from '../SearchBar';
import { HelpLink } from '@/features/help';

export type InventoryTab = 'inventory' | 'availability' | 'counterparty-reports';

type TabNavigationBarProps = {
  activeTab: InventoryTab;
  onTabChange: (tab: InventoryTab) => void;
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;
  searchDisabled: boolean;
  searchResultCount: number | undefined;
  appliedFiltersCount: number;
  appliedFiltersExpanded: boolean;
  onToggleAppliedFiltersExpanded: () => void;
};

const TAB_BUTTON_CLASSES = 'px-3 py-1.5 text-lg font-semibold transition-colors';

const TabButton: React.FC<{
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  dataTour?: string;
}> = ({ active, onClick, children, dataTour }) => (
  <button
    onClick={onClick}
    data-tour={dataTour}
    className={clsx(
      TAB_BUTTON_CLASSES,
      active
        ? 'text-gray-900 border-b-2 border-blue-600'
        : 'text-gray-500 hover:text-gray-700'
    )}
  >
    {children}
  </button>
);

export const TabNavigationBar: React.FC<TabNavigationBarProps> = ({
  activeTab,
  onTabChange,
  searchQuery,
  onSearchQueryChange,
  searchDisabled,
  searchResultCount,
  appliedFiltersCount,
  appliedFiltersExpanded,
  onToggleAppliedFiltersExpanded,
}) => {
  return (
    <div className="flex flex-row items-center justify-between gap-4">
      <div className="flex items-center gap-6">
        <div className="flex items-center gap-1 border-b-2 border-transparent">
          <TabButton active={activeTab === 'inventory'} onClick={() => onTabChange('inventory')}>
            Inventory
          </TabButton>
          <TabButton
            active={activeTab === 'availability'}
            onClick={() => onTabChange('availability')}
          >
            Availability Report
          </TabButton>
          <TabButton
            active={activeTab === 'counterparty-reports'}
            onClick={() => onTabChange('counterparty-reports')}
            dataTour="counterparty-reports-tab-button"
          >
            Counterparty Reports
          </TabButton>
        </div>
      </div>
      <div className="flex items-center gap-3">
        {appliedFiltersCount > 0 && (
          <button
            type="button"
            onClick={onToggleAppliedFiltersExpanded}
            className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 focus:outline-none"
            aria-expanded={appliedFiltersExpanded}
            aria-label="Applied filters"
          >
            Applied filters: {appliedFiltersCount}
            {appliedFiltersExpanded ? (
              <ChevronUpIcon className="h-4 w-4 text-blue-600" aria-hidden />
            ) : (
              <ChevronDownIcon className="h-4 w-4 text-blue-600" aria-hidden />
            )}
          </button>
        )}
        <div className="w-80" data-tour="inventory-search">
          <SearchBar
            value={searchQuery}
            onChange={onSearchQueryChange}
            disabled={searchDisabled}
            resultCount={searchResultCount}
          />
        </div>
        <HelpLink to="inventory" iconOnly label="Inventory docs" />
      </div>
    </div>
  );
};
